import { useEffect, useState } from "react";
import { useFieldArray, useWatch, type Control } from "react-hook-form";

import Button from "@/components/Button";
import IconButton from "@/components/IconButton";
import type { MetricCardType } from "@/components/MetricCard/MetricCard.types";
import MetricGrid from "@/components/MetricGrid";
import SubSectionHeading from "@/components/SubSectionHeading";
import { ControlledTextField } from "@/components/TextField";
import type { Path } from "@/router";

import AutoFocusedPlanField from "./_AutoFocusedPlanField";
import CareerPlanEmptySection, { CAREER_PLAN_SECTION_ICONS } from "./_CareerPlanEmptySection";
import SortablePlanItems from "./_SortablePlanItems";
import {
  EMPTY_HIGHLIGHT,
  MAX_CAREER_PLAN_CARDS,
  type CareerPlanDocumentFormValues,
  type CareerPlanHighlightFields
} from "./careerPlanDocument";

type CardFieldName = "strengths" | "focusAreas";

type HeadlineDescriptionListProps = {
  control: Control<CareerPlanDocumentFormValues>;
  name: CardFieldName;
  noun: string;
  addLabel: string;
  emptyTitle: string;
  readOnly: boolean;
  canManage: boolean;
  disabled?: boolean;
  onBeginEdit: () => void;
};

export default function HeadlineDescriptionList({
  control,
  name,
  noun,
  addLabel,
  emptyTitle,
  readOnly,
  canManage,
  disabled = false,
  onBeginEdit
}: HeadlineDescriptionListProps) {
  const { fields, append, move, remove } = useFieldArray({ control, name });
  const items = useWatch({ control, name });
  const canAdd = canManage && fields.length < MAX_CAREER_PLAN_CARDS;
  const [focusCardIndex, setFocusCardIndex] = useState(0);

  useEffect(() => {
    if (readOnly) setFocusCardIndex(0);
  }, [readOnly]);

  const addItem = () => {
    if (disabled) return;
    if (readOnly) onBeginEdit();
    setFocusCardIndex(fields.length);
    append(EMPTY_HIGHLIGHT);
  };

  const addButton = canAdd ? (
    <Button type="button" variant="filledTonal" icon="add" onClick={addItem} disabled={disabled} testId={`add-${name}`}>
      {addLabel}
    </Button>
  ) : null;

  if (fields.length === 0) {
    return (
      <CareerPlanEmptySection
        section={name}
        title={emptyTitle}
        addLabel={addLabel}
        canManage={canAdd}
        disabled={disabled}
        onAdd={addItem}
      />
    );
  }

  if (readOnly) {
    return (
      <div data-testid={`career-plan-${name}`}>
        <MetricGrid metrics={viewMetrics(name, items ?? fields, noun)} layout="thirds" hasHeading />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid={`career-plan-${name}`}>
      <SortablePlanItems
        fields={fields}
        onMove={move}
        onDragStart={() => setFocusCardIndex(-1)}
        dragTestId={(index) => `reorder-${name}-${index}`}
      >
        {(_field, index, dragHandle) => (
          <div
            className="flex flex-col gap-4 rounded-xl border border-sys-brand-outline-variant p-4"
            data-testid={`career-plan-${name}-card-${index}`}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-2">
                {dragHandle}
                <SubSectionHeading>{`${noun} ${index + 1}`}</SubSectionHeading>
              </div>
              <IconButton
                name="delete"
                skipMargin
                ariaLabel={`Remove ${noun.toLowerCase()} ${index + 1}`}
                title={`Remove ${noun.toLowerCase()} ${index + 1}`}
                onClick={() => remove(index)}
                data-testid={`remove-${name}-${index}`}
              />
            </div>
            <AutoFocusedPlanField
              control={control}
              name={`${name}.${index}.title`}
              label={`${noun} ${index + 1} Title`}
              autoFocus={index === focusCardIndex}
            />
            <ControlledTextField
              control={control}
              name={`${name}.${index}.description`}
              label={`${noun} ${index + 1} Description`}
              type="textarea"
              rows={3}
            />
          </div>
        )}
      </SortablePlanItems>
      {addButton && <div>{addButton}</div>}
    </div>
  );
}

function viewMetrics(name: CardFieldName, items: CareerPlanHighlightFields[], noun: string): MetricCardType<Path>[] {
  return items.map((item, index) => ({
    iconName: CAREER_PLAN_SECTION_ICONS[name],
    value: item.title.trim() || `${noun} ${index + 1}`,
    label: item.description,
    testId: `career-plan-${name}-card-${index}`
  }));
}
